import { Star, Coins, Award, LucideIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useGetMyPointsHistoryQuery } from '@/redux/points/points.api.slice';
import type { PointsHistoryRecord } from '@/redux/points/points.types';
import Pager from './Pager';
import { LedgerEmpty, LedgerSkeleton } from './LedgerStates';

const LIMIT = 10;

/** A make hangs off either a post or a project group; link to whichever it is. */
function makeTarget(
  record: PointsHistoryRecord
): { title: string; url: string } | null {
  const make = record.resourceId;
  if (!make) return null;
  if (make.post) {
    return {
      title: make.post.title,
      url: `/model/${make.post._id}#make-${make._id}`,
    };
  }
  if (make.projectGroup) {
    return {
      title: make.projectGroup.name,
      url: `/project-groups/${make.projectGroup._id}#make-${make._id}`,
    };
  }
  return null;
}

/** Ledger rows that carry their own reason instead of a make. */
const REASON_ROWS: Record<string, { label: string; icon: LucideIcon }> = {
  adminAdjustment: { label: 'Admin adjustment', icon: Coins },
  achievement: { label: 'Achievement', icon: Award },
};

function ReviewSource({ record }: { record: PointsHistoryRecord }) {
  const target = makeTarget(record);
  const reviewer = record.resourceId?.user?.username;
  return (
    <>
      {target ? (
        <Link
          to={target.url}
          className="block truncate text-sm font-medium hover:text-green-700 hover:underline"
        >
          {target.title}
        </Link>
      ) : (
        <span className="text-sm font-medium">Qualifying review</span>
      )}
      {reviewer && (
        <Link
          to={`/${reviewer}`}
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          by @{reviewer}
        </Link>
      )}
    </>
  );
}

const ICON_TONE = {
  reason: 'bg-green-50 text-green-700',
  review: 'bg-amber-100 text-amber-700',
};

function ReasonSource({ label, reason }: { label: string; reason?: string }) {
  return (
    <>
      <p className="text-sm font-medium">{label}</p>
      {reason && (
        <p className="truncate text-xs text-muted-foreground">{reason}</p>
      )}
    </>
  );
}

function PointsCell({ record }: { record: PointsHistoryRecord }) {
  const credit = record.points >= 0;
  return (
    <div className="shrink-0 text-right">
      <p
        className={cn(
          'font-display text-base font-semibold tabular-nums',
          credit ? 'text-green-700' : 'text-destructive-subtle'
        )}
      >
        {credit ? `+${record.points}` : record.points}
      </p>
      <p className="text-xs tabular-nums text-muted-foreground">
        {format(new Date(record.createdAt), 'MMM d, yyyy')}
      </p>
    </div>
  );
}

function HistoryRow({ record }: { record: PointsHistoryRecord }) {
  const reason = REASON_ROWS[record.resourceType];
  const Icon = reason ? reason.icon : Star;
  return (
    <li className="flex items-center gap-3 py-3">
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          ICON_TONE[reason ? 'reason' : 'review']
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        {reason ? (
          <ReasonSource label={reason.label} reason={record.reason} />
        ) : (
          <ReviewSource record={record} />
        )}
      </div>
      <PointsCell record={record} />
    </li>
  );
}

export default function HistoryPanel() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGetMyPointsHistoryQuery({
    page,
    limit: LIMIT,
  });

  if (isLoading) return <LedgerSkeleton />;
  if (!data || data.records.length === 0) {
    return (
      <LedgerEmpty>
        No points earned yet. Upload a CNC file to get started!
      </LedgerEmpty>
    );
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y">
        {data.records.map((record) => (
          <HistoryRow key={record._id} record={record} />
        ))}
      </ul>
      <Pager page={page} total={data.total} limit={LIMIT} onPage={setPage} />
    </div>
  );
}
